import { useEffect, useRef, useState } from "react";
import { EXAMPLE_PROMPTS } from "@/constants/task-templates";
import { shuffleArray } from "@/lib/utils/shuffle";

/**
 * Hook to rotate placeholder text with smooth animations.
 *
 * Shuffles EXAMPLE_PROMPTS on mount and cycles every 6 seconds with fade transitions.
 * Respects user's reduced motion preferences and pauses when input is focused
 * or has content.
 *
 * @param focused - Whether the input is currently focused
 * @param hasContent - Whether the input has any content
 * @returns Current placeholder string and visibility state
 */
export function usePlaceholderRotation(focused: boolean, hasContent: boolean) {
	const [prompts] = useState(() => shuffleArray(EXAMPLE_PROMPTS));
	const [index, setIndex] = useState(0);
	const [placeholderVisible, setPlaceholderVisible] = useState(true);
	const focusedRef = useRef(focused);
	const hasContentRef = useRef(hasContent);

	// Keep refs in sync
	useEffect(() => {
		focusedRef.current = focused;
	}, [focused]);

	useEffect(() => {
		hasContentRef.current = hasContent;
	}, [hasContent]);

	useEffect(() => {
		const prefersReduced =
			typeof window !== "undefined" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		if (prefersReduced) return;

		// Use setInterval for consistent 6-second cycles
		const intervalId = setInterval(() => {
			// Check conditions using refs (current values, not stale closures)
			if (!focusedRef.current && !hasContentRef.current) {
				// Fade out
				setPlaceholderVisible(false);

				// Swap text and fade back in after transition
				setTimeout(() => {
					setIndex((prev) => (prev + 1) % prompts.length);
					setPlaceholderVisible(true);
				}, 200); // Match CSS transition duration
			}
		}, 6000);

		return () => clearInterval(intervalId);
	}, [prompts.length]); // Empty deps - interval runs consistently

	return { placeholder: prompts[index], placeholderVisible };
}
